import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XIcon, CalendarIcon, ClockIcon, CheckCircleIcon, XCircleIcon, EditIcon, ArchiveIcon, UserIcon, CreditCardIcon, LandmarkIcon, WalletIcon, FileTextIcon, ShieldCheckIcon, AlertCircleIcon, DownloadIcon, SmartphoneIcon, PaperclipIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../mocks/mockBills';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
export function BillDetail() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [, forceUpdate] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const bill = mockBills.find(b => b.id === id) || mockBills[0];
  const currentUser = 'Simon Verraest';
  // Look up supplier for payout details
  const supplier = mockSuppliers.find(s => s.id === bill.supplierId);
  // Derived state logic
  const isScheduled = bill.status === 'approved' && bill.approvalStatus === 'approved';
  const isDue = bill.status === 'approved' && !isScheduled;
  const isPending = bill.status === 'pending_approval';
  const isPaid = bill.status === 'paid';
  const isRejected = bill.status === 'rejected';
  const isOverdue = bill.status === 'overdue';
  const isAssignedToMe = bill.approver === currentUser;
  const displayStatus = isPaid ? 'Paid' : isRejected ? 'Rejected' : isScheduled ? 'Scheduled' : isPending ? 'Pending Approval' : isOverdue ? 'Overdue' : isDue ? 'Due' : 'Draft';
  const getStatusBadgeClass = () => {
    if (isPaid) return 'bg-green-50 text-green-700 border-green-200';
    if (isRejected || isOverdue) return 'bg-red-50 text-red-700 border-red-200';
    if (isScheduled) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (isPending) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };
  // Handlers
  const handleApprove = () => {
    approveBill(bill.id, currentUser);
    forceUpdate(n => n + 1);
  };
  const handleReject = () => {
    rejectBill(bill.id, currentUser, rejectNotes);
    setShowRejectModal(false);
    setRejectNotes('');
    forceUpdate(n => n + 1);
  };
  const handleArchive = () => {
    updateBill(bill.id, {
      status: 'rejected',
      approvalNotes: 'Archived by user'
    });
    forceUpdate(n => n + 1);
  };
  const handleCancelSchedule = () => {
    updateBill(bill.id, {
      status: 'approved',
      approvalStatus: 'pending',
      approver: undefined
    });
    forceUpdate(n => n + 1);
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      currency: bill.currency,
      fractionDigits: 2
    });
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  // Helper: get payout method display details from supplier
  const getPayoutDetails = () => {
    if (!supplier) return null;
    if (supplier.payoutMethod === 'bank_account') {
      return {
        type: 'Bank Account',
        icon: <LandmarkIcon size={16} className="text-gray-400" data-id="element-3779" data-mp="jx84cz4kejbr" />,
        details: [{
          label: 'Bank',
          value: supplier.bankName || '—'
        }, {
          label: 'Account',
          value: supplier.accountNumber ? '•••' + supplier.accountNumber.slice(-4) : '—'
        }, {
          label: 'Account Name',
          value: supplier.accountName || '—'
        }]
      };
    }
    return {
      type: 'PromptPay',
      icon: <SmartphoneIcon size={16} className="text-gray-400" data-id="element-3780" data-mp="1b0msoxgsq1d4" />,
      details: [{
        label: 'Type',
        value: supplier.promptPayType === 'phone' ? 'Phone Number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'
      }, {
        label: 'ID',
        value: supplier.promptPayId ? supplier.promptPayId.slice(0, 3) + '••••' + supplier.promptPayId.slice(-3) : '—'
      }, {
        label: 'Name',
        value: supplier.promptPayName || '—'
      }]
    };
  };
  // Helper: get payment source display
  const getPaymentSourceLabel = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return BRAND.balanceLabel;
      case 'card':
        return 'Corporate Card';
      case 'bank_transfer':
        return 'Bank Transfer';
      default:
        return 'Unknown';
    }
  };
  const getPaymentSourceIcon = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return <WalletIcon size={16} className="text-gray-400" data-id="element-3781" data-mp="3vt1jcmadw1hg" />;
      case 'card':
        return <CreditCardIcon size={16} className="text-gray-400" data-id="element-3782" data-mp="3qtyccwaufl6u" />;
      default:
        return <LandmarkIcon size={16} className="text-gray-400" data-id="element-3783" data-mp="8o274tgfbhx9" />;
    }
  };
  // ===== TIMELINE LOGIC (corrected) =====
  // Step statuses: 'completed' | 'current' | 'future' | 'rejected'
  const approvalCompleted = bill.approvalStatus === 'approved' || isScheduled || isPaid || isDue; // isDue means status='approved', so approval happened
  const approvalRejected = bill.approvalStatus === 'rejected' || isRejected;
  const steps = [{
    id: 'created',
    label: 'Bill Created',
    date: bill.issueDate,
    actor: bill.createdBy || currentUser,
    status: 'completed' as const
  }, {
    id: 'approval',
    label: 'Approval',
    date: approvalCompleted || approvalRejected ? bill.approvalDate : undefined,
    actor: approvalCompleted ? bill.approver || 'Auto-approved' : approvalRejected ? bill.approver || 'System' : isPending ? bill.approver || 'Awaiting assignment' : '—',
    status: approvalCompleted ? 'completed' as const : approvalRejected ? 'rejected' as const : isPending ? 'current' as const : 'future' as const
  }, {
    id: 'scheduled',
    label: 'Scheduled',
    date: isScheduled || isPaid ? bill.dueDate : undefined,
    actor: isScheduled || isPaid ? 'System' : undefined,
    status: isScheduled || isPaid ? 'completed' as const : 'future' as const
  }, {
    id: 'payment',
    label: 'Payment',
    date: isPaid ? bill.paidDate : undefined,
    actor: isPaid ? getPaymentSourceLabel() : undefined,
    status: isPaid ? 'completed' as const : isScheduled ? 'current' as const : 'future' as const
  }];
  const payoutDetails = getPayoutDetails();
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-3784" data-mp="3cn0kp2t5irxn">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3785" data-mp="37ne8mwp27hqf">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors" data-id="element-3786" data-mp="37krqy48gfluh">
          <ArrowLeftIcon size={18} data-id="element-3787" data-mp="46xc8uu7sz89" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-3788" data-mp="3ve9xbky7xwtg">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3789" data-mp="2jxa5srjp3m0h">
          {/* Card 1: Header & Total */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm" data-id="element-3790" data-mp="2juxf9pzvg3bt">
            <div className="flex justify-between items-start mb-6" data-id="element-3791" data-mp="2f0bd8dfojn4e">
              <div data-id="element-3792" data-mp="34lkufli26ct7">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2" data-id="element-3793" data-mp="34l1ajz8ulecj">
                  {bill.billNumber}
                </h1>
                <p className="text-gray-500" data-id="element-3794" data-mp="2vlxs8xhi92z3">
                  {bill.description || 'No description provided'}
                </p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeClass()}`} data-id="element-3795" data-mp="2znrmoo13xaqt">
                {displayStatus}
              </span>
            </div>
            <div className="pt-6 border-t border-gray-100" data-id="element-3796" data-mp="3ekif2dcd5m6x">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-3797" data-mp="3ek0jzk1fte0l">
                {t('pages.BillDetail.total_amount')}
              </div>
              <div className="text-4xl font-bold text-gray-900" data-id="element-3798" data-mp="a628ajo8wy22">
                {formatCurrency(bill.total)}
              </div>
            </div>
          </div>

          {/* Card: Dates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3799" data-mp="1zmnyygckcmit">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-3800" data-mp="1uow71mbx50em">
              <div data-id="element-3801" data-mp="1umidxj0sty9p">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3802" data-mp="29czez19ium62">
                  {t('pages.BillDetail.issue_date')}
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3803" data-mp="24gied7l4yfc3">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3804" data-mp="1gqo48xs7b4vj" />
                  {formatDate(bill.issueDate)}
                </div>
              </div>
              <div data-id="element-3805" data-mp="1qnsqsypyl2cv">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3806" data-mp="1qoc94gjitsrf">
                  {t('pages.BillDetail.due_date')}
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3807" data-mp="320v85vzvtvy3">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3808" data-mp="2x3mmhlfiz9s3" />
                  {formatDate(bill.dueDate)}
                </div>
              </div>
              {bill.approvalDate && approvalCompleted && <div data-id="element-3809" data-mp="2du69ct2i34v0">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3810" data-mp="2dtn1642njuu6">
                    {t('pages.BillDetail.approval_date')}
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3811" data-mp="242inr8smki1g">
                    <CheckCircleIcon size={14} className="text-gray-400" data-id="element-3812" data-mp="1z4qoftpnhtn8" />
                    {formatDate(bill.approvalDate)}
                  </div>
                </div>}
              {bill.paidDate && <div data-id="element-3813" data-mp="1q5d9ilirccv4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3814" data-mp="1l463os06dnr3">
                    {t('pages.BillDetail.paid_date')}
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3815" data-mp="3kf7f86n2ybr9">
                    <CheckCircleIcon size={14} className="text-green-500" data-id="element-3816" data-mp="3fg4a6m73o5ja" />
                    {formatDate(bill.paidDate)}
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Card: Supplier */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3817" data-mp="n6op0c1f7h1">
          <div className="flex justify-between items-center mb-6" data-id="element-3818" data-mp="5lhwl5irv7wm">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2" data-id="element-3819" data-mp="5n2v1687jazr">
              <UserIcon size={16} className="text-gray-400" data-id="element-3820" data-mp="391oz38pgaz3t" />
              {t('pages.BillDetail.supplier_details')}
            </h3>
            <button onClick={() => navigate(`/supplier-detail/${bill.supplierId}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-3821" data-mp="3e00dlwqoxxsd">
              {t('pages.BillDetail.view_profile')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3822" data-mp="y8ugf7xjt1m8">
            <div data-id="element-3823" data-mp="2ucs9ja4rfh7j">
              <div className="text-xs text-gray-500 mb-1" data-id="element-3824" data-mp="2uc91cl4ww76p">
                {t('pages.BillDetail.company_name')}
              </div>
              <div className="text-sm font-medium text-gray-900" data-id="element-3825" data-mp="2pdpe8hel0pcr">
                {bill.supplierName}
              </div>
            </div>
            <div data-id="element-3826" data-mp="2zcellg8uqrer">
              <div className="text-xs text-gray-500 mb-1" data-id="element-3827" data-mp="2zbveyvonjpg1">
                {t('pages.BillDetail.contact_email')}
              </div>
              <div className="text-sm font-medium text-gray-900" data-id="element-3828" data-mp="2bn2dkjp9gpe1">
                {bill.supplierEmail}
              </div>
            </div>
            {bill.supplierPhone && <div data-id="element-3829" data-mp="2kfw81oda3vch">
                <div className="text-xs text-gray-500 mb-1" data-id="element-3830" data-mp="2kgffgb5az1c9">
                  {t('pages.BillDetail.phone_number')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-3831" data-mp="1ryjumjuum87s">
                  {bill.supplierPhone}
                </div>
              </div>}
          </div>
        </div>

        {/* Card: Payment Information (Paid/Scheduled) */}
        {(isPaid || isScheduled) && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3832" data-mp="1dr6rvnksq8u8">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-3833" data-mp="1iluk9xix7ne3">
              <CreditCardIcon size={16} className="text-gray-400" data-id="element-3834" data-mp="18tya3o3k86ti" />
              {t('pages.BillDetail.payment_information')}
            </h3>

            {isPaid ? <div className="space-y-6" data-id="element-3835" data-mp="1wkapfk14x5u4">
                {/* Payment Source */}
                <div data-id="element-3836" data-mp="21haze613usqi">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-3837" data-mp="21gjcrkphk5cd">
                    {t('pages.BillDetail.funded_from')}
                  </div>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-3838" data-mp="1riv4dryyizbq">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-3839" data-mp="b4v6snnl3lrn">
                      {getPaymentSourceIcon()}
                    </div>
                    <div data-id="element-3840" data-mp="wd7aetlj5xs4">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-3841" data-mp="wdqk5n111fv2">
                        {getPaymentSourceLabel()}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-3842" data-mp="mlt5g6at85rl">
                        {t('pages.BillDetail.paid_on')}{' '}
                        {formatDate(bill.paidDate)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payout Destination */}
                {payoutDetails && <div data-id="element-3843" data-mp="1aa420x9h3ja2">
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-3844" data-mp="15cu2gosddr8i">
                      {t('pages.BillDetail.paid_to')}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-3845" data-mp="10gdatklyouqo">
                      <div className="flex items-center gap-3 mb-4" data-id="element-3846" data-mp="3q18byxjzbx7z">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-3847" data-mp="3l3gmea6t4n6y">
                          {payoutDetails.icon}
                        </div>
                        <div className="text-sm font-semibold text-gray-900" data-id="element-3848" data-mp="25mbwqhmsqofu">
                          {payoutDetails.type}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200" data-id="element-3849" data-mp="2fblqs317ah6l">
                        {payoutDetails.details.map(d => <div key={d.label} data-id="element-3850" data-mp="2kc9eofu4uhwl">
                            <div className="text-xs text-gray-500 mb-0.5" data-id="element-3851" data-mp="2ob0e4tm2mbmv">
                              {d.label}
                            </div>
                            <div className="text-sm font-medium text-gray-900" data-id="element-3852" data-mp="2t8s4vkazbrph">
                              {d.value}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>}
              </div> : <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-3853" data-mp="vupchaffgrn7">
                <ClockIcon size={18} className="text-blue-600 mt-0.5" data-id="element-3854" data-mp="rup0rxjpquju" />
                <div data-id="element-3855" data-mp="15t57hohksl1z">
                  <div className="font-medium text-sm" data-id="element-3856" data-mp="15tolwv80orb9">
                    {t('pages.BillDetail.scheduled_for_payment')}
                  </div>
                  <div className="text-xs mt-1 text-blue-700" data-id="element-3857" data-mp="10werf15rtpgk">
                    {t('pages.BillDetail.this_bill_is_scheduled_to_be')}{' '}
                    {formatDate(bill.dueDate)}.
                  </div>
                </div>
              </div>}
          </div>}

        {/* Card: Attachment (replaces Line Items) */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3858" data-mp="1jkjnrnk3m85c">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-3859" data-mp="1yhu71j2zgibm">
            <PaperclipIcon size={16} className="text-gray-400" data-id="element-3860" data-mp="1tjshcfb1xkr6" />
            {t('pages.BillDetail.attachment')}
          </h3>

          {bill.attachmentFileName ? <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-3861" data-mp="2h6e54hns6us3">
              <div className="flex items-center gap-3 min-w-0" data-id="element-3862" data-mp="2374rnpqymert">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-3863" data-mp="238plte9d3vp5">
                  <FileTextIcon size={20} className="text-red-500" data-id="element-3864" data-mp="1tgtnq37a05kx" />
                </div>
                <div className="min-w-0" data-id="element-3865" data-mp="1tez1mlk9q7f9">
                  <div className="text-sm font-medium text-gray-900 truncate" data-id="element-3866" data-mp="1y7s390kfwzn4">
                    {bill.attachmentFileName}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3867" data-mp="1ke0wu1209vvm">
                    {t('pages.BillDetail.uploaded_with_bill')}
                  </div>
                </div>
              </div>
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#9F140F] hover:bg-[#FEE7E5] rounded-lg transition-colors flex-shrink-0" data-id="element-3868" data-mp="1aippw4wu9syf">
                <DownloadIcon size={16} aria-hidden="true" data-id="element-3869" data-mp="1uy0oda6qfsg1" />
                {t('pages.BillDetail.download')}
              </button>
            </div> : <div className="text-center py-8 text-gray-400" data-id="element-3870" data-mp="1lydkmhqj6ajt">
              <PaperclipIcon size={24} className="mx-auto mb-2 opacity-50" data-id="element-3871" data-mp="1lwst70zij7q6" />
              <p className="text-sm" data-id="element-3872" data-mp="1gy7rsy8iyi05">
                {t('pages.BillDetail.no_file_attached_to_this_bill')}
              </p>
            </div>}

          {/* Amount Summary */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end" data-id="element-3873" data-mp="1797m6vzioocz">
            <div className="w-full max-w-xs space-y-3" data-id="element-3874" data-mp="137md7lq3su82">
              <div className="flex justify-between text-sm" data-id="element-3875" data-mp="13848af1152ee">
                <span className="text-gray-500" data-id="element-3876" data-mp="y9koak5zy0oo">
                  {t('pages.BillDetail.subtotal')}
                </span>
                <span className="text-gray-900 font-medium" data-id="element-3877" data-mp="t96ofpejtx1q">
                  {formatCurrency(bill.subtotal)}
                </span>
              </div>
              {bill.taxAmount && bill.taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-3878" data-mp="e6f1uu62mljx">
                  <span className="text-gray-500" data-id="element-3879" data-mp="j4q8yx4zehyg">
                    {t('pages.BillDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-3880" data-mp="o5dxnc5qmmpi">
                    {formatCurrency(bill.taxAmount)}
                  </span>
                </div>}
              {bill.discountAmount && bill.discountAmount > 0 && <div className="flex justify-between text-sm" data-id="element-3881" data-mp="f0fqegva48n">
                  <span className="text-gray-500" data-id="element-3882" data-mp="fjpv909zoc4">
                    {t('pages.BillDetail.discount')}
                  </span>
                  <span className="text-red-600 font-medium" data-id="element-3883" data-mp="a539igvtbm81">
                    -{formatCurrency(bill.discountAmount)}
                  </span>
                </div>}
              <div className="flex justify-between pt-3 border-t border-gray-200" data-id="element-3884" data-mp="38t91p3ovvwru">
                <span className="text-base font-bold text-gray-900" data-id="element-3885" data-mp="3dsbyk0upmwoq">
                  {t('pages.BillDetail.total')}
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-3886" data-mp="1evy58t4xospr">
                  {formatCurrency(bill.total)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card: Notes */}
        {bill.notes && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3887" data-mp="1nw3aawmnnf0r">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3888" data-mp="r86u3sv4qlq2">
              {t('pages.BillDetail.notes')}
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 border border-gray-100" data-id="element-3889" data-mp="106s7l9lqvajz">
              {bill.notes}
            </div>
          </div>}
      </div>

      {/* RIGHT COLUMN */}
      <div className="lg:col-span-1 space-y-6" data-id="element-3890" data-mp="8gxhvbtz5ius">
        {/* ACTIONS CARD */}
        <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isPending && isAssignedToMe ? 'border-l-4 border-l-[#E2231A] border-y-gray-200 border-r-gray-200' : 'border-gray-200'}`} data-id="element-3891" data-mp="chhemeamykge">
          <div className="p-6" data-id="element-3892" data-mp="3w49j11dhbtgo">
            {/* DUE */}
            {isDue && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-5" data-id="element-3893" data-mp="3s24s4aef1axq">
                  {t('pages.BillDetail.actions')}
                </h3>
                <div className="space-y-3" data-id="element-3894" data-mp="3i4qcue48as70">
                  <button onClick={() => navigate(`/bill-approval/schedule?ids=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#C41B14] text-sm font-medium shadow-sm transition-colors" data-id="element-3895" data-mp="3i5iaduhdyzzx">
                    <CalendarIcon size={16} data-id="element-3896" data-mp="2zes5y1c6354y" />
                    {t('pages.BillDetail.schedule_payment')}
                  </button>
                  <button onClick={handleArchive} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-3897" data-mp="2uggujm684mkm">
                    <ArchiveIcon size={16} data-id="element-3898" data-mp="2aunpr4s64vt6" />
                    {t('pages.BillDetail.archive_bill')}
                  </button>
                  <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[#9F140F] hover:bg-[#FEE7E5] rounded-lg text-sm font-medium transition-colors mt-2" data-id="element-3899" data-mp="2fujig7vf10tf">
                    <EditIcon size={16} aria-hidden="true" data-id="element-3900" data-mp="2tryevwk368uw" />
                    {t('pages.BillDetail.edit_details')}
                  </button>
                </div>
              </>}

            {/* PENDING */}
            {isPending && <>
                {isAssignedToMe ? <>
                    <div className="flex items-center gap-2 mb-3" data-id="element-3901" data-mp="1fsdcshrj1khn">
                      <ShieldCheckIcon size={18} className="text-[#9F140F]" data-id="element-3902" data-mp="1fqk5d093aw8l" />
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-3903" data-mp="1at0rw2k7unhp">
                        {t('pages.BillDetail.approval_required')}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-6" data-id="element-3904" data-mp="1klg9gyrlpa22">
                      {t('pages.BillDetail.this_bill_has_been_assigned_to')}
                    </p>
                    <div className="space-y-3" data-id="element-3905" data-mp="rvprufqrfq17">
                      <button onClick={handleApprove} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#C41B14] text-sm font-medium shadow-sm transition-colors" data-id="element-3906" data-mp="rx0xkl3rn7uy">
                        <CheckIcon size={16} data-id="element-3907" data-mp="2hbgwotcbnih9" />
                        {t('pages.BillDetail.approve_bill')}
                      </button>
                      <button onClick={() => setShowRejectModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors" data-id="element-3908" data-mp="38iz6qsx5ibnr">
                        <XIcon size={16} data-id="element-3909" data-mp="3mirv4hl7gfsk" />
                        {t('pages.BillDetail.reject_bill')}
                      </button>
                    </div>
                  </> : <>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3910" data-mp="3vgk99if6xhed">
                      {t('pages.BillDetail.status')}
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3911" data-mp="92kf593c92p3">
                      <ClockIcon size={18} className="text-yellow-600 mt-0.5" data-id="element-3912" data-mp="e02cs3bwnvhs" />

                      <div data-id="element-3913" data-mp="j1rpdnes0h7z">
                        <div className="text-sm font-medium text-yellow-900" data-id="element-3914" data-mp="iyvr1xz0nqj0">
                          {t('pages.BillDetail.awaiting_approval')}
                        </div>
                        <div className="text-xs text-yellow-700 mt-1" data-id="element-3915" data-mp="1yimq7vphaeuz">
                          {t('pages.BillDetail.assigned_to')}{' '}
                          <span className="font-semibold" data-id="element-3916" data-mp="1om1kf85mwzq3">
                            {bill.approver || 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>}
              </>}

            {/* SCHEDULED */}
            {isScheduled && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3917" data-mp="2chmbquhp2dmx">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3 mb-6" data-id="element-3918" data-mp="351umbl45elf0">
                  <CalendarIcon size={18} className="text-blue-600 mt-0.5" data-id="element-3919" data-mp="3088lcq7ck5z1" />
                  <div data-id="element-3920" data-mp="ydc68feeumet">
                    <div className="text-sm font-medium text-blue-900" data-id="element-3921" data-mp="ycsvpjr3b0at">
                      {t('pages.BillDetail.payment_scheduled')}
                    </div>
                    <div className="text-xs text-blue-700 mt-1" data-id="element-3922" data-mp="ol6ff8zwab17">
                      {t('pages.BillDetail.will_be_paid_on')}{' '}
                      {formatDate(bill.dueDate)}
                    </div>
                  </div>
                </div>
                <button onClick={handleCancelSchedule} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-3923" data-mp="fnw45nn2ulwa">
                  <XIcon size={16} aria-hidden="true" data-id="element-3924" data-mp="aoj7ycabg2h1" />
                  {t('pages.BillDetail.cancel_schedule')}
                </button>
              </>}

            {/* PAID */}
            {isPaid && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3925" data-mp="3phz0lptkwz5z">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3926" data-mp="3khkqm28f3f5b">
                  <CheckCircleIcon size={18} className="text-green-600 mt-0.5" data-id="element-3927" data-mp="3uhldtz9eqfes" />

                  <div data-id="element-3928" data-mp="c2klj1nzphcq">
                    <div className="text-sm font-medium text-green-900" data-id="element-3929" data-mp="c1ra3ne363o9">
                      {t('pages.BillDetail.payment_complete')}
                    </div>
                    <div className="text-xs text-green-700 mt-1" data-id="element-3930" data-mp="m21ohbbuxh50">
                      {t('pages.BillDetail.successfully_paid_on')}{' '}
                      {formatDate(bill.paidDate)}
                    </div>
                  </div>
                </div>
              </>}

            {/* REJECTED */}
            {isRejected && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3931" data-mp="1x0w3tp0je3a8">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3932" data-mp="21y4r240jkxic">
                  <XCircleIcon size={18} className="text-red-600 mt-0.5" data-id="element-3933" data-mp="26ssgq67a7xyi" />
                  <div data-id="element-3934" data-mp="2boh9nqosqglu">
                    <div className="text-sm font-medium text-red-900" data-id="element-3935" data-mp="2brva2tdwgfqn">
                      {t('pages.BillDetail.bill_rejected')}
                    </div>
                    <div className="text-xs text-red-700 mt-1" data-id="element-3936" data-mp="1j268adtd8bnz">
                      {t('pages.BillDetail.rejected_by')}{' '}
                      {bill.approver || 'System'}
                    </div>
                  </div>
                </div>
                {bill.approvalNotes && <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-3937" data-mp="2zg4r7i064fbg">
                    <span className="font-medium text-gray-900 block mb-1" data-id="element-3938" data-mp="33fx4g80ib9n5">
                      {t('pages.BillDetail.reason')}
                    </span>
                    {bill.approvalNotes}
                  </div>}
              </>}
          </div>
        </div>

        {/* TIMELINE CARD */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3939" data-mp="izq8cnxeg20p">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-3940" data-mp="e1f0giqo01l4">
            {t('pages.BillDetail.workflow_timeline')}
          </h3>

          <div className="space-y-0 relative" data-id="element-3941" data-mp="44abkb5fps0m">
            {steps.map((step, index) => {
            const nextStep = steps[index + 1];
            // Line is dark only if current step is completed AND next step is also completed or current
            const lineDark = step.status === 'completed' && nextStep && (nextStep.status === 'completed' || nextStep.status === 'current');
            return <div key={step.id} className="flex gap-4 relative" data-id="element-3942" data-mp="1pfivztcwhxsa" data-mpi={index}>
                  {/* Connector Line */}
                  {index < steps.length - 1 && <div className={`absolute left-[11px] top-7 bottom-[-7px] w-0.5 ${lineDark ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-3943" data-mp="2arefpl8isdqu" />}

                  {/* Indicator */}
                  <div className="relative z-10 flex flex-col items-center mt-1" data-id="element-3944" data-mp="2kkd7td386mf7">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${step.status === 'completed' ? 'bg-gray-900 text-white' : step.status === 'current' ? 'bg-white border-2 border-[#E2231A]' : step.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-gray-100 border border-gray-200'}`} data-id="element-3945" data-mp="1wsokkb59go54">
                      {step.status === 'completed' && <CheckIcon size={12} strokeWidth={3} data-id="element-3946" data-mp="3c96wlz7p180e" />}
                      {step.status === 'current' && <div className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-3947" data-mp="3kp4lyb1x3gxp" />}
                      {step.status === 'rejected' && <XIcon size={12} strokeWidth={3} data-id="element-3948" data-mp="3arzwa18v53c5" />}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pb-8 min-w-0 flex-1" data-id="element-3949" data-mp="3frcsvdpjdorx">
                    <div className="flex items-center justify-between" data-id="element-3950" data-mp="7b1577708eo6">
                      <span className={`text-sm font-medium ${step.status === 'future' ? 'text-gray-400' : step.status === 'rejected' ? 'text-red-700' : 'text-gray-900'}`} data-id="element-3951" data-mp="cae1sjnoh03y">
                        {step.label}
                      </span>
                      {step.date && step.status !== 'future' && <span className="text-xs text-gray-500" data-id="element-3952" data-mp="q8khqkso8ffc">
                          {formatDate(step.date)}
                        </span>}
                    </div>
                    {step.status === 'current' ? <div className="text-xs mt-1 text-[#9F140F] font-medium flex items-center gap-1" data-id="element-3953" data-mp="zxrcz9jup1k2">
                        <ClockIcon size={12} data-id="element-3954" data-mp="uyeji5yh4w8i" /> {t('pages.BillDetail.awaiting')}
                      </div> : step.actor && step.status !== 'future' ? <div className="text-xs mt-1 text-gray-500" data-id="element-3955" data-mp="qz5sh8utjcgn">
                        {step.actor}
                      </div> : null}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div> /* Reject Modal */;
  {
    /* Reject Modal */
  }
  {
    showRejectModal && <>
        <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-3956" data-mp="7aol309ojp2s" />

        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-3957" data-mp="39bgetibprxg">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-3958" data-mp="3ufwa6kzrjdy6">
            <div className="p-8" data-id="element-3959" data-mp="29vhh9ttotuoa">
              <div className="flex items-center gap-3 mb-4" data-id="element-3960" data-mp="2eulmyahela9v">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-3961" data-mp="2et0uqrika3f6">
                  <AlertCircleIcon size={20} className="text-red-600" data-id="element-3962" data-mp="2ix8mkxkbfzhm" />
                </div>
                <h2 className="text-lg font-bold text-gray-900" data-id="element-3963" data-mp="2nuqv4muf8cp3">
                  {t('pages.BillDetail.reject_bill')}
                </h2>
              </div>
              <p className="text-sm text-gray-600 mb-6" data-id="element-3964" data-mp="1v635yg6jx369">
                {t('pages.BillDetail.are_you_sure_you_want_to')}{' '}
                <span className="font-medium text-gray-900" data-id="element-3965" data-mp="204efepsxpbny">
                  {bill.billNumber}
                </span>
                {t('pages.BillDetail.this_action_will_notify_the_submitter')}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3966" data-mp="17h0g4l1rk40o">
                {t('pages.BillDetail.reason_for_rejection')}
              </label>
              <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={3} placeholder={t('pages.BillDetail.attrs.please_provide_a_reason')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-colors resize-none" data-id="element-3967" data-mp="3f43h83bty7h7" />

              <div className="flex justify-end gap-3 mt-8" data-id="element-3968" data-mp="3sufaw6o2ceag">
                <button onClick={() => {
                setShowRejectModal(false);
                setRejectNotes('');
              }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3969" data-mp="3stx4wiblmlpc">
                  {t('pages.BillApprovalWizard.cancel')}
                </button>
                <button onClick={handleReject} disabled={!rejectNotes.trim()} className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${rejectNotes.trim() ? 'bg-red-600 hover:bg-red-700 shadow-sm' : 'bg-red-300 cursor-not-allowed'}`} data-id="element-3970" data-mp="28gojeprva4hm">
                  {t('pages.BillDetail.reject_bill')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>;
  }
}